import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import type { DevelopmentDetailController } from '@/presentation/controllers/development-detail-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { useAssistedJiraLink } from '@/presentation/hooks/use-assisted-jira-link.js';
import { AssistedJiraLinkDialog } from '@/presentation/pages/developments/development-detail/components/dialogs/assisted-jira-link-dialog.js';

interface Props {
    developmentId: string;
    jiraEpicKey: string | null;
    epicCreateOpen: boolean;
    canLink: boolean;
    controller: DevelopmentDetailController;
    onLinked: () => Promise<void>;
}

export function AssistedJiraLinkSection({ developmentId, jiraEpicKey, epicCreateOpen, canLink, controller, onLinked }: Props) {
    const { translate } = useApp();
    const link = useAssistedJiraLink(controller, developmentId, onLinked);

    if (!canLink || jiraEpicKey || epicCreateOpen) {
        return null;
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Button
                data-testid="assisted-jira-link-open"
                variant="outlined"
                onClick={() => {
                    void link.openDialog();
                }}
            >
                {translate('devDetail.jiraLinkOpen')}
            </Button>
            <AssistedJiraLinkDialog
                key={link.generation}
                open={link.open}
                suggestion={link.suggestion}
                epics={link.epics}
                occupiedError={link.occupiedError}
                failureMessage={link.failureMessage}
                loading={link.loading}
                submitting={link.submitting}
                labels={{
                    title: translate('devDetail.jiraLinkTitle'),
                    suggestionLabel: translate('devDetail.jiraLinkSuggestion'),
                    listLabel: translate('devDetail.jiraLinkList'),
                    confirmLabel: translate('devDetail.jiraLinkConfirm'),
                    noPairLabel: translate('devDetail.jiraLinkNoPair'),
                    cancelLabel: translate('devDetail.jiraLinkCancel'),
                    emptyLabel: translate('devDetail.jiraLinkEmpty'),
                    loadingLabel: translate('devDetail.jiraLinkLoading'),
                    occupiedWarning: (displayId) => translate('devDetail.jiraLinkOccupied', { displayId })
                }}
                onConfirm={(epicKey) => {
                    void link.confirm(epicKey);
                }}
                onNoPair={() => {
                    void link.confirmNoPair();
                }}
                onClose={link.close}
            />
        </Box>
    );
}
